// Thin typed client for the Agency OS backend. URLs are prefixed by API_BASE:
// empty everywhere the page is served over http(s) — web (Vite proxy or SPA
// served by the VPS) AND the native shell, whose WebView loads the SPA from
// the backend itself since #39 (cookie first-party ; WKWebView/ITP discards
// cross-site fetch cookies, which broke the embedded-SPA login).
// https://api.omosa.co only as a fallback if the page ever loads from a
// capacitor:// origin (legacy embedded build).

const NATIVE_API_DEFAULT = "https://api.omosa.co";

/**
 * Base de l'API : "" dès que l'origine est http(s) (même origine),
 * https://api.omosa.co en secours (origine capacitor:// d'un build embarqué).
 * Surchargeable via localStorage `aos_api_base` (préprod/debug) — poser la
 * valeur dans la console WebView puis recharger (⌘R fonctionne dans la coquille).
 */
export const API_BASE: string = (() => {
  try {
    const override = localStorage.getItem("aos_api_base");
    if (override) return override.replace(/\/+$/, "");
  } catch {
    /* localStorage indisponible (très improbable) → défauts */
  }
  return window.location.origin.startsWith("http") ? "" : NATIVE_API_DEFAULT;
})();

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON content-type when we actually send a body: Fastify rejects
  // a body-less POST that carries content-type: application/json with a 400
  // ("Body cannot be empty…"), which broke every no-body action (WhatsApp pairing,
  // Slack/Gmail login, Matrix provision, account start/stop, …).
  const hasBody = init?.body != null;
  const res = await fetch(`${API_BASE}/api${path}`, {
    credentials: "include",
    headers: {
      ...(hasBody ? { "content-type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      /* non-JSON body */
    }
    throw new ApiError(res.status, msg);
  }
  // 204 / empty body
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// --- Auth ------------------------------------------------------------------

// GET /api/auth/me returns the tenant context the middleware resolved from the
// session cookie (no profile fields yet — that grows with the members module).
export interface Me {
  memberId: string;
  orgId: string;
  // Identité du membre courant (renseignée par core/auth) — alimente le menu
  // compte du header. Optionnelles : absentes sur un backend plus ancien.
  displayName?: string | null;
  email?: string | null;
}

// /auth/me always answers 200; `authenticated:false` is the logged-out signal
// (avoids a 401 console error on the cold login screen).
interface MeResponse {
  authenticated: boolean;
  memberId?: string;
  orgId?: string;
  displayName?: string | null;
  email?: string | null;
}

export const auth = {
  me: async (): Promise<Me | null> => {
    const r = await request<MeResponse>("/auth/me");
    if (!r.authenticated || !r.memberId || !r.orgId) return null;
    return {
      memberId: r.memberId,
      orgId: r.orgId,
      displayName: r.displayName ?? null,
      email: r.email ?? null,
    };
  },
  login: (email: string, password: string) =>
    request<{ ok: true }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  /** Accepte une invitation (PUBLIC) : crée le membre + auto-login. */
  acceptInvite: (token: string, displayName: string, password: string) =>
    request<{ ok?: true; memberId?: string }>("/auth/accept-invite", {
      method: "POST",
      body: JSON.stringify({ token, displayName, password }),
    }),
};

// Invitations d'équipe (#25) — onboarding par e-mail.
export interface Invite {
  id?: string;
  inviteId?: string;
  email: string;
  projectId?: string | null;
  role?: string;
  expiresAt?: string;
}
export const invites = {
  create: (email: string, projectId: string | null, role: string) =>
    request<{ inviteId: string; token: string; expiresAt: string }>("/auth/invites", {
      method: "POST",
      body: JSON.stringify({ email, projectId, role }),
    }),
  list: () => request<{ invites: Invite[] }>("/auth/invites"),
  revoke: (id: string) =>
    request<{ revoked: boolean }>(`/auth/invites/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

// --- Messaging -------------------------------------------------------------

export interface Conversation {
  id: string;
  /** Id de la room sur la plateforme (#22) — rattache typing/présence à la conversation. */
  remoteId?: string | null;
  accountId: string;
  network: string;
  kind: string;
  title: string;
  isArchived: boolean;
  isPinned: boolean;
  /** Unread STATE: new incoming messages OR manually marked unread. */
  isUnread: boolean;
  /** Unread message count for the badge (0 → show a dot if isUnread). */
  unreadCount: number;
  lastMessageAt: string | null;
  /** Aperçu du dernier message (style Beeper) — peut être vide. */
  lastMessageBody?: string | null;
  /** Le dernier message porte un média (→ aperçu « Pièce jointe » si pas de texte). */
  lastMessageHasMedia?: boolean;
  /**
   * Nom d'affichage de l'expéditeur (dernier correspondant entrant connu, depuis
   * le roster). Pour les e-mails, sert de titre de la conversation (objet en
   * aperçu). Null si inconnu.
   */
  lastMessageSenderName?: string | null;
  /** Projet de rattachement (#18/#32), null si non cloisonnée. */
  projectId?: string | null;
  /**
   * Catégories/labels portables de la conversation (Gmail). Tokens :
   * "category:primary|social|promotions|updates|forums" (onglets Gmail) +
   * "label:<nom>" (labels perso). Absent/null pour les réseaux sans labels.
   * Pilote la vue « Principal » par défaut + les filtres par catégorie/label.
   */
  labels?: string[] | null;
}

export interface ConversationState {
  archived?: boolean;
  pinned?: boolean;
  unread?: boolean;
}

/** Résumé renvoyé par /conversations/by-identity (CRM niveau 2, #51). */
export interface IdentityConversation {
  conversationId: string;
  network: string;
  kind: string;
  title: string;
  lastMessageAt: string | null;
  lastMessageBody: string | null;
  unreadCount: number;
  isArchived: boolean;
  isPinned: boolean;
}

// Stored shapes (cf modules/messaging: MediaRef[] + reactions jsonb).
export interface MediaRef {
  kind: "image" | "video" | "audio" | "voice" | "file";
  mimeType?: string;
  fileName?: string;
}
export interface Reaction {
  emoji: string;
  count: number;
  mine: boolean;
}

/** Un membre d'une conversation de groupe (roster du connecteur). */
export interface Member {
  remoteId: string;
  name: string;
  isSelf: boolean;
}

export interface Message {
  id: string;
  /** L'id du message sur la plateforme — permet de résoudre replyToRemoteId (#21). */
  remoteId?: string | null;
  senderRemoteId: string | null;
  /** Nom d'affichage de l'expéditeur (fils de groupe) — joint depuis le roster (#112). */
  senderDisplayName?: string | null;
  isOutgoing: boolean;
  /** Le membre est mentionné (« @moi ») dans ce message — surligne la bulle. */
  isSelfMentioned?: boolean;
  body: string;
  /** Corps HTML riche (e-mails) — rendu sanitizé côté front ; null en chat. */
  bodyHtml?: string | null;
  /** Destinataires e-mail (To/Cc avec noms) — affichés « À / Cc » dans le fil. */
  recipients?: {
    to?: { email: string; name?: string }[];
    cc?: { email: string; name?: string }[];
  } | null;
  media: MediaRef[] | null;
  buttons: unknown;
  reactions: Reaction[] | null;
  replyToRemoteId?: string | null;
  sentAt: string | null;
}

/** Une réponse de fil pertinente (suivi / mention) — centre de notifs (incr. 2). */
export interface ThreadReplyNotif {
  id: string;
  conversationId: string;
  body: string;
  sentAt: string | null;
  senderRemoteId: string | null;
  senderDisplayName: string | null;
  conversationTitle: string;
  network: string;
  /** remoteId du message racine du fil — pour ouvrir le panneau de thread. */
  rootRemoteId: string | null;
  isSelfMentioned: boolean;
  /** « mention » = on t'a mentionné · « participant » = tu suis ce fil. */
  reason: "mention" | "participant";
  /** Nombre de réponses récentes pertinentes dans ce fil (1 ligne par fil). */
  replyCount: number;
}

/** Un message récent où le membre est mentionné (« @moi ») — centre de notifs. */
export interface Mention {
  id: string;
  conversationId: string;
  body: string;
  sentAt: string | null;
  senderRemoteId: string | null;
  senderDisplayName: string | null;
  conversationTitle: string;
  network: string;
}

export const messaging = {
  conversations: () => request<{ conversations: Conversation[] }>("/messaging/conversations"),
  /** Conversations impliquant une identité réseau {network, remoteId} (CRM niveau 2, #51). */
  conversationsByIdentity: (network: string, remoteId: string) =>
    request<{ conversations: IdentityConversation[] }>(
      `/messaging/conversations/by-identity?network=${encodeURIComponent(
        network,
      )}&remoteId=${encodeURIComponent(remoteId)}`,
    ),
  messages: (conversationId: string) =>
    request<{ messages: Message[] }>(
      `/messaging/conversations/${encodeURIComponent(conversationId)}/messages`,
    ),
  /** Messages récents mentionnant le membre, tous fils confondus. */
  mentions: () => request<{ mentions: Mention[] }>("/messaging/mentions"),
  /** Réponses récentes dans des fils suivis / où le membre est mentionné. */
  threadActivity: () => request<{ replies: ThreadReplyNotif[] }>("/messaging/thread-activity"),
  /** Un thread complet (racine + réponses) — panneau de thread façon Slack. */
  thread: (messageId: string) =>
    request<{ root: Message; replies: Message[] }>(
      `/messaging/messages/${encodeURIComponent(messageId)}/thread`,
    ),
  send: (
    conversationId: string,
    body: string,
    attachmentUploadIds?: string[],
    replyToRemoteId?: string,
    mentions?: Array<{ display: string; remoteId: string }>,
    asVoice?: boolean,
    /** Destinataires + corps HTML e-mail (reply-all / forward / To-Cc-Cci / WYSIWYG). */
    email?: { to?: string[]; cc?: string[]; bcc?: string[]; subject?: string; bodyHtml?: string },
  ) =>
    request<{ messageId: string; remoteId: string }>(
      `/messaging/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: "POST",
        body: JSON.stringify({
          body,
          attachmentUploadIds,
          replyToRemoteId,
          mentions,
          asVoice,
          to: email?.to,
          cc: email?.cc,
          bcc: email?.bcc,
          subject: email?.subject,
          bodyHtml: email?.bodyHtml,
        }),
      },
    ),
  /** Ajoute (ou retire, remove=true) une réaction emoji sur un message (#21). */
  react: (messageId: string, emoji: string, remove = false) =>
    request<{ ok: boolean }>(`/messaging/messages/${encodeURIComponent(messageId)}/react`, {
      method: "POST",
      body: JSON.stringify({ emoji, remove }),
    }),
  /** Stage une pièce jointe (multipart) → uploadId à référencer dans send(). */
  upload: async (file: File): Promise<{ uploadId: string; fileName: string; mimeType: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    // Pas de content-type manuel : le navigateur pose le boundary multipart.
    const res = await fetch(`${API_BASE}/api/messaging/uploads`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) throw new ApiError(res.status, res.statusText);
    return res.json();
  },
  // URL servie par le backend (cookie-auth) — utilisable directement en <img>/<audio> src.
  /** Roster d'un groupe (noms réels) — pour l'autocomplete de mention (#6). 501 si non groupe. */
  members: (conversationId: string) =>
    request<{ members: Member[] }>(
      `/messaging/conversations/${encodeURIComponent(conversationId)}/members`,
    ),
  mediaUrl: (messageId: string) =>
    `${API_BASE}/api/messaging/messages/${encodeURIComponent(messageId)}/media`,
  /** Proxy d'une image DISTANTE d'un corps d'e-mail (cookie-auth, <img> src, anti-SSRF). */
  emailImageUrl: (remoteUrl: string) =>
    `${API_BASE}/api/messaging/email-image?url=${encodeURIComponent(remoteUrl)}`,
  /** Image INLINE (cid:) d'un corps d'e-mail (cookie-auth, <img> src). */
  inlineImageUrl: (messageId: string, contentId: string) =>
    `${API_BASE}/api/messaging/messages/${encodeURIComponent(messageId)}/inline?cid=${encodeURIComponent(contentId)}`,
  /** Photo de la conversation (cookie-auth, <img> src). 404 → repli initiales. */
  conversationAvatarUrl: (conversationId: string) =>
    `${API_BASE}/api/messaging/conversations/${encodeURIComponent(conversationId)}/avatar`,
  /** Photo d'un expéditeur dans un groupe (cookie-auth, <img> src). 404/501 → repli initiales (#112). */
  participantAvatarUrl: (conversationId: string, remoteId: string) =>
    `${API_BASE}/api/messaging/conversations/${encodeURIComponent(conversationId)}/participants/${encodeURIComponent(remoteId)}/avatar`,
  /** Photo d'un contact dans le panneau « Nouveau message » (cookie-auth, <img> src). 204 → initiales. */
  contactAvatarUrl: (accountId: string, remoteId: string) =>
    `${API_BASE}/api/messaging/accounts/${encodeURIComponent(accountId)}/contacts/${encodeURIComponent(remoteId)}/avatar`,
  /** Signale une conversation e-mail comme spam (Gmail : label SPAM + hors INBOX). */
  gmailSpam: (conversationId: string) =>
    request<{ ok: boolean }>(
      `/messaging/gmail/conversations/${encodeURIComponent(conversationId)}/spam`,
      { method: "POST" },
    ),
  /** Rattache (ou détache, projectId=null) une conversation à un projet (#18). */
  setProject: (conversationId: string, projectId: string | null) =>
    request<{ id: string }>(
      `/messaging/conversations/${encodeURIComponent(conversationId)}/project`,
      { method: "PATCH", body: JSON.stringify({ projectId }) },
    ),
  /** Bascule les flags locaux d'une conversation (archivé/épinglé/non-lu). */
  setState: (conversationId: string, state: ConversationState) =>
    request<{ id: string }>(
      `/messaging/conversations/${encodeURIComponent(conversationId)}/state`,
      { method: "PATCH", body: JSON.stringify(state) },
    ),
  /** Marque lu : vide le badge local + envoie un vrai accusé de lecture à la
   *  plateforme (jusqu'au dernier message). Côté serveur, fan-out SSE read_changed. */
  markRead: (conversationId: string) =>
    request<{ ok: boolean }>(
      `/messaging/conversations/${encodeURIComponent(conversationId)}/read`,
      { method: "POST" },
    ),
  /**
   * Démarre une nouvelle conversation sur le compte choisi (compose). Le handle
   * est interprété par le connecteur (numéro WhatsApp, email Gmail, @username
   * Telegram…). 404 si le compte n'est pas à toi, 503 si son connecteur ne tourne
   * pas / ne sait pas démarrer (ex. LinkedIn).
   */
  startConversation: (handle: string, accountId: string) =>
    request<{ conversationRemoteId: string; title: string; accountId: string }>(
      "/messaging/conversations/start",
      { method: "POST", body: JSON.stringify({ handle, accountId }) },
    ),
  /**
   * Recherche dans le carnet du compte (autocomplete compose). WhatsApp seulement
   * pour l'instant ; les autres réseaux renvoient [] (l'UI garde la saisie libre).
   * Le `handle` d'un résultat est ce qu'attend startConversation.
   */
  searchContacts: (q: string, accountId: string) =>
    request<{ contacts: Contact[]; accountId?: string }>(
      `/messaging/contacts?q=${encodeURIComponent(q)}&accountId=${encodeURIComponent(accountId)}`,
    ),
};

/** Un contact du carnet (résultat de recherche compose). */
export interface Contact {
  remoteId: string;
  handle: string;
  name: string;
  /** "chat" = conversation existante ; "directory" = carnet (groupement Beeper). */
  source: "chat" | "directory";
}

/** La pièce jointe principale du message (la 1re), ou null. */
export function primaryMedia(m: Message): MediaRef | null {
  return Array.isArray(m.media) && m.media.length > 0 ? m.media[0]! : null;
}

// --- Comptes / connexions --------------------------------------------------

export interface Account {
  id: string;
  network: string;
  displayName: string;
  matrixUserId: string | null;
  /** Identité du membre sur le réseau (e-mail pour Gmail) — exclusion « répondre à tous ». */
  remoteSelfId?: string | null;
}

export const accounts = {
  list: () => request<{ accounts: Account[] }>("/messaging/accounts"),
  status: (id: string) =>
    request<{
      accountId: string;
      network: string;
      running: boolean;
      /** Supervision : "backoff" = crashé, relance auto en cours ; "crashed" = abandon (start manuel requis). */
      state?: "running" | "backoff" | "crashed" | "stopped";
      restartAttempts?: number;
    }>(`/messaging/accounts/${encodeURIComponent(id)}/status`),
  start: (id: string) =>
    request<{ status?: string }>(`/messaging/accounts/${encodeURIComponent(id)}/start`, {
      method: "POST",
    }),
  stop: (id: string) =>
    request<{ stopped: boolean }>(`/messaging/accounts/${encodeURIComponent(id)}/stop`, {
      method: "POST",
    }),
  /** Déconnecte le compte : l'arrête + le supprime AVEC toutes ses conversations (cascade). */
  disconnect: (id: string) =>
    request<{ deleted: boolean }>(`/messaging/accounts/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  /** Reconnecte tous les comptes du membre (best-effort). */
  reconnect: () => request<{ started?: number }>("/messaging/reconnect", { method: "POST" }),
  /**
   * Re-synchronise les conversations Gmail déjà ingérées avec Gmail : labels/INBOX
   * (vue « Principal » + filtres) ET état lu/non-lu (le badge AOS reflète le label
   * UNREAD de Gmail). `all` rafraîchit toutes les conversations (sync complète),
   * sinon seulement celles sans labels. Best-effort.
   */
  gmailRelabel: (all = false) =>
    request<{ updated: number; scanned: number; capped: boolean }>(
      `/messaging/gmail/relabel${all ? "?all=true" : ""}`,
      { method: "POST" },
    ),
  /**
   * Backfill l'historique de la boîte Gmail : la réception live ne prend que le
   * courrier reçu depuis la connexion, donc l'inbox existante doit être tirée
   * explicitement (threads INBOX → ingestion des absents). Best-effort.
   */
  gmailSyncInbox: () =>
    request<{ ingested: number; scanned: number; capped: boolean }>("/messaging/gmail/sync-inbox", {
      method: "POST",
    }),
};

// --- Branchement d'un compte (onboarding) ----------------------------------

/** Provisionne le puppet Matrix du membre (prérequis WhatsApp/mautrix). */
export const matrix = {
  provision: () =>
    request<{ matrixUserId: string; created: boolean }>("/messaging/matrix/provision", {
      method: "POST",
    }),
};

// État renvoyé par le polling de pairing WhatsApp : soit un succès
// ({status:"connected"}), soit une mise à jour ({kind:"qr"|"error"|…}).
export interface WhatsAppPoll {
  status?: "connected";
  accountId?: string;
  running?: boolean;
  kind?: "qr" | "success" | "error" | "pairing" | string;
  message?: string;
}

export const whatsappLogin = {
  begin: () =>
    request<{ loginId: string; kind?: string }>("/messaging/whatsapp/login", { method: "POST" }),
  poll: (id: string) =>
    request<WhatsAppPoll>(`/messaging/whatsapp/login/${encodeURIComponent(id)}`),
  cancel: (id: string) =>
    request<{ cancelled: boolean }>(`/messaging/whatsapp/login/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  qrUrl: (id: string) =>
    `${API_BASE}/api/messaging/whatsapp/login/${encodeURIComponent(id)}/qr.png`,
};

/** OAuth user-token (Slack, Gmail) : renvoie l'URL d'autorisation à ouvrir. */
export const oauth = {
  start: (network: "slack" | "gmail") =>
    request<{ authorizeUrl: string; state: string }>(`/messaging/${network}/login`, {
      method: "POST",
    }),
};

// Pairing mautrix par COOKIE (LinkedIn = li_at ; Google Chat = cookies Google).
// Même flux que WhatsApp QR mais on soumet un cookie au lieu de scanner.
export type CookieNet = "linkedin" | "gchat";
export interface PairPoll {
  status?: "success" | "connected";
  kind?: "success" | "error" | "cookie" | "pairing" | string;
  message?: string;
}
export const cookieLogin = {
  begin: (net: CookieNet) =>
    request<{ loginId: string; kind?: string }>(`/messaging/${net}/login`, { method: "POST" }),
  submitCookie: (net: CookieNet, id: string, cookie: string) =>
    request<PairPoll>(`/messaging/${net}/login/${encodeURIComponent(id)}/cookie`, {
      method: "POST",
      body: JSON.stringify({ cookie }),
    }),
  poll: (net: CookieNet, id: string) =>
    request<PairPoll>(`/messaging/${net}/login/${encodeURIComponent(id)}`),
  cancel: (net: CookieNet, id: string) =>
    request<unknown>(`/messaging/${net}/login/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

// Telegram MTProto : multi-étapes, clé par membre (pas de loginId).
export type TelegramState = "waiting_code" | "waiting_password" | "connected" | string;
// Temps réel : flux SSE des événements du membre (#21). En même origine,
// EventSource envoie le cookie tout seul ; en natif (API_BASE non vide,
// cross-origin) il faut withCredentials pour porter le cookie de session.
export const events = {
  open: () =>
    new EventSource(`${API_BASE}/api/messaging/events`, { withCredentials: API_BASE !== "" }),
  /** Noms d'événements émis par le serveur (cf contrat messaging). */
  MESSAGE_RECEIVED: "messaging.message.received",
  CONNECTION_STATUS: "messaging.connection.status_changed",
  TYPING: "messaging.typing.changed",
  PRESENCE: "messaging.presence.changed",
  READ_CHANGED: "messaging.conversation.read_changed",
};

export interface AgentSuggestion {
  id: string;
  conversationId: string;
  network: string;
  suggestion: string;
  status: string;
  createdAt: string;
}

// Journal d'audit (#23) — trace immuable des actions sensibles (org-scopée).
export interface AuditEntry {
  id: number;
  actorId: string | null;
  action: string;
  target: string | null;
  projectId: string | null;
  meta: unknown;
  at: string;
}
export const audit = {
  list: (limit = 100) => request<{ events: AuditEntry[] }>(`/audit?limit=${limit}`),
};

// Tenancy (Phase 3) : clients + projets (cloisonnement).
export interface Client {
  id: string;
  name: string;
}
export interface Project {
  id: string;
  name: string;
  status?: string;
  clientId?: string;
}
export interface ProjectMember {
  memberId: string;
  role: string;
  displayName?: string;
  email?: string;
}
export const tenancy = {
  clients: () => request<{ clients: Client[] }>("/clients"),
  createClient: (name: string) =>
    request<{ clientId: string }>("/clients", { method: "POST", body: JSON.stringify({ name }) }),
  /** Supprime un client (cascade vers ses projets côté backend). */
  deleteClient: (id: string) =>
    request<{ deleted: boolean }>(`/clients/${encodeURIComponent(id)}`, { method: "DELETE" }),
  projects: () => request<{ projects: Project[] }>("/projects"),
  createProject: (clientId: string, name: string) =>
    request<{ projectId: string }>("/projects", {
      method: "POST",
      body: JSON.stringify({ clientId, name }),
    }),
  /** Supprime un projet (lead-only côté RLS ; cascade vers les membres). */
  deleteProject: (id: string) =>
    request<{ deleted: boolean }>(`/projects/${encodeURIComponent(id)}`, { method: "DELETE" }),
  members: (projectId: string) =>
    request<{ members: ProjectMember[] }>(`/projects/${encodeURIComponent(projectId)}/members`),
  setMemberRole: (projectId: string, memberId: string, role: string) =>
    request<unknown>(
      `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberId)}`,
      { method: "PUT", body: JSON.stringify({ role }) },
    ),
  removeMember: (projectId: string, memberId: string) =>
    request<unknown>(
      `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberId)}`,
      { method: "DELETE" },
    ),
};

// Dossier unifié de l'interlocuteur d'une conversation (#27 incr. 7).
export interface Dossier {
  personId: string;
  displayName: string;
  companies: Array<{
    companyId?: string;
    name: string;
    role?: string;
    optedIn?: boolean;
    domain?: string;
    hasLogo?: boolean;
  }>;
  colleagues: Array<{ displayName: string; role?: string }>;
  memory: Array<{ kind: string; content: string }>;
  relevant: Array<{ content: string; score: number }>;
  optedIn: boolean;
}

// Hermes (#27) : proposer une réponse pour une conversation, puis l'envoyer.
export const agent = {
  /** Dossier unifié (personne + entreprises + collègues + mémoire + extraits). */
  dossier: (conversationId: string) =>
    request<{ dossier: Dossier | null; reason?: string }>(
      `/agent/conversations/${encodeURIComponent(conversationId)}/dossier`,
    ),
  /** Brouillons en attente (tous, ou d'une conversation). */
  list: (conversationId?: string) =>
    request<{ suggestions: AgentSuggestion[] }>(
      `/agent/suggestions${conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : ""}`,
    ),
  suggest: (
    conversationId: string,
    opts?: { consent?: boolean; instruction?: string; draft?: string },
  ) =>
    request<{ suggestionId?: string; suggestion: string | null; reason?: string }>(
      `/agent/conversations/${encodeURIComponent(conversationId)}/suggest`,
      { method: "POST", body: JSON.stringify(opts ?? {}) },
    ),
  send: (suggestionId: string) =>
    request<{ sent: boolean; messageId: string }>(
      `/agent/suggestions/${encodeURIComponent(suggestionId)}/send`,
      { method: "POST" },
    ),
  dismiss: (suggestionId: string) =>
    request<{ dismissed: boolean }>(
      `/agent/suggestions/${encodeURIComponent(suggestionId)}/dismiss`,
      { method: "POST" },
    ),
  /** Signal d'apprentissage : devenir d'une suggestion (#27 P5.3). */
  feedback: (suggestionId: string, outcome: "sent" | "edited" | "dismissed", finalText?: string) =>
    request<{ recorded: boolean }>(
      `/agent/suggestions/${encodeURIComponent(suggestionId)}/feedback`,
      { method: "POST", body: JSON.stringify({ outcome, finalText }) },
    ),
};

// Knowledge-graph (#10) : centre de validation des fusions de contacts. Quand un
// nouveau compte réseau ressemble à une personne déjà connue (même e-mail / tél /
// nom), le backend dépose une suggestion en attente ; on la valide ou l'écarte ici.
export interface MatchSuggestion {
  id: string;
  reason: "email" | "phone" | "name";
  status: "pending" | "approved" | "declined";
  personA: { id: string; displayName: string };
  personB: { id: string; displayName: string };
  createdAt: string;
}
// Une identité réseau d'une personne (compte WhatsApp/Slack/… rattaché).
export interface PersonIdentity {
  id: string;
  network: string;
  remoteId: string;
  handle: string;
  displayName: string;
}
export interface Person {
  id: string;
  displayName: string;
  /** Prénom / Nom structurés (#74) — recomposent le nom affiché à l'édition. */
  firstName?: string | null;
  lastName?: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  /** Champs CRM (#11) : LinkedIn, statut de relation, tags libres. */
  linkedinUrl?: string | null;
  relationStatus?: string | null;
  tags?: string[];
  /** Identités réseau (network, remoteId) — pour la photo dans la liste (#68). */
  identities?: Array<{ network: string; remoteId: string }>;
  /** Opt-in IA par contact : autorise l'envoi de son contexte à l'API Claude. */
  aiSuggestionsOptIn?: boolean;
  /** Modèle A (#1) : true = contact du CRM ; false = identité observée à confirmer. */
  confirmed?: boolean;
  updatedAt?: string;
}
export interface PersonDetail {
  person: Person;
  identities: PersonIdentity[];
}
export interface DuplicateSuggestion {
  id: string;
  displayName: string;
  reason: "email" | "phone" | "name";
}

export const kg = {
  matchSuggestions: (status: "pending" | "approved" | "declined" = "pending") =>
    request<{ suggestions: MatchSuggestion[] }>(
      `/knowledge-graph/match-suggestions?status=${status}`,
    ),
  /** Valider → fusionne les deux personnes (le mieux nommé survit). */
  approveMatch: (id: string) =>
    request<{ personId: string; merged: boolean }>(
      `/knowledge-graph/match-suggestions/${encodeURIComponent(id)}/approve`,
      { method: "POST" },
    ),
  /** Écarter → mémorisé, la paire n'est plus jamais reproposée. */
  declineMatch: (id: string) =>
    request<{ id: string; status: string }>(
      `/knowledge-graph/match-suggestions/${encodeURIComponent(id)}/decline`,
      { method: "POST" },
    ),
  /** Toutes les personnes du membre (recherche d'une cible de fusion). */
  persons: () => request<{ persons: Person[] }>("/knowledge-graph/persons"),
  /** Contacts créés récemment (7 j) — notif « nouveau contact → compléter » (#10). */
  recentPersons: () =>
    request<{
      recent: Array<{
        id: string;
        displayName: string;
        primaryEmail: string | null;
        primaryPhone: string | null;
        createdAt: string;
      }>;
    }>("/knowledge-graph/persons/recent"),
  /** Une personne + ses identités réseau. */
  person: (id: string) =>
    request<PersonDetail>(`/knowledge-graph/persons/${encodeURIComponent(id)}`),
  /** Geste « Ajouter le contact » (#1) : promeut une identité observée en contact
   *  du CRM, sans fusion. Renvoie la personne à jour. */
  confirmPerson: (id: string) =>
    request<PersonDetail>(`/knowledge-graph/persons/${encodeURIComponent(id)}/confirm`, {
      method: "POST",
    }),
  /** « Ignorer » serveur : sort une identité observée de la file « à confirmer »
   *  de façon persistante (≠ masquage local). Réversible via { ignored: false }. */
  ignorePerson: (id: string, ignored = true) =>
    request<{ ok: true; ignored: boolean }>(
      `/knowledge-graph/persons/${encodeURIComponent(id)}/ignore`,
      { method: "POST", body: JSON.stringify({ ignored }) },
    ),
  /** Supprime un contact (#3) — cascade identités/mémoire/corpus/appartenances. */
  deletePerson: (id: string) =>
    request<{ deleted: boolean }>(`/knowledge-graph/persons/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  /** File « nouveaux contacts à confirmer » (#1) : identités observées non confirmées. */
  pendingPersons: () =>
    request<{
      pending: Array<{
        id: string;
        displayName: string;
        primaryEmail: string | null;
        primaryPhone: string | null;
        createdAt: string;
        identities: Array<{ network: string; remoteId: string }>;
      }>;
    }>("/knowledge-graph/persons/pending"),
  /** Édite un contact (prénom/nom/e-mail/téléphone/entreprise). Renvoie la personne à jour. */
  updatePerson: (
    id: string,
    patch: {
      displayName?: string;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      phone?: string | null;
      linkedinUrl?: string | null;
      relationStatus?: string | null;
      tags?: string[] | null;
      companyName?: string | null;
    },
  ) =>
    request<PersonDetail>(`/knowledge-graph/persons/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  /** Ajoute une note de mémoire sur un contact (#5). */
  addPersonMemory: (personId: string, content: string, kind = "note") =>
    request<{ memoryId: string }>(
      `/knowledge-graph/persons/${encodeURIComponent(personId)}/memory`,
      { method: "POST", body: JSON.stringify({ content, kind }) },
    ),
  /** Supprime les doublons fantômes (personnes orphelines). Renvoie le nombre supprimé. */
  cleanupOrphans: () =>
    request<{ deleted: number }>("/knowledge-graph/persons/cleanup-orphans", { method: "POST" }),
  /** Résout les noms depuis la messagerie + efface les faux téléphones. */
  backfillNames: () =>
    request<{
      upgraded: number;
      scanned: number;
      phonesCleared: number;
      namesSplit: number;
      suggested: number;
    }>("/knowledge-graph/persons/backfill-names", { method: "POST" }),
  /** Synchro carnet : matche {nom, téléphones, emails} → renomme les noms faibles. */
  matchContacts: (contacts: Array<{ name: string; phones?: string[]; emails?: string[] }>) =>
    request<{ matched: number; renamed: number }>("/knowledge-graph/contacts/match", {
      method: "POST",
      body: JSON.stringify({ contacts }),
    }),
  /** Résout la personne EXISTANTE d'une identité réseau (lecture seule). 404 si non résolue. */
  personByIdentity: (network: string, remoteId: string) =>
    request<PersonDetail>(
      `/knowledge-graph/persons/by-identity?network=${encodeURIComponent(network)}&remoteId=${encodeURIComponent(remoteId)}`,
    ),
  /** Doublons probables d'une personne (même e-mail/tél/nom). */
  suggestions: (id: string) =>
    request<{ suggestions: DuplicateSuggestion[] }>(
      `/knowledge-graph/persons/${encodeURIComponent(id)}/suggestions`,
    ),
  /** Fusionne la source dans la cible (déplace identités/mémoire/corpus). */
  merge: (targetId: string, sourceId: string) =>
    request<{ moved: number }>("/knowledge-graph/persons/merge", {
      method: "POST",
      body: JSON.stringify({ targetId, sourceId }),
    }),
  /** Démerge : détache une identité dans une nouvelle personne. */
  split: (personId: string, identityId: string) =>
    request<{ personId: string; identityId: string }>(
      `/knowledge-graph/persons/${encodeURIComponent(personId)}/identities/${encodeURIComponent(
        identityId,
      )}/split`,
      { method: "POST" },
    ),
  /** Opt-in IA par contact : autorise (ou retire) l'envoi de son contexte à Claude. */
  setPersonOptIn: (personId: string, optIn: boolean) =>
    request<{ personId: string; optIn: boolean }>(
      `/knowledge-graph/persons/${encodeURIComponent(personId)}/ai-opt-in`,
      { method: "PATCH", body: JSON.stringify({ optIn }) },
    ),
  /** Liste des entreprises connues (pour associer une conversation à une entreprise). */
  companies: () => request<{ companies: KgCompany[] }>("/knowledge-graph/companies"),
  /** Crée une entreprise ; avec un domaine, le back matche les emails (idempotent). */
  createCompany: (name: string, domain?: string) =>
    request<{ companyId: string; domain?: string }>("/knowledge-graph/companies", {
      method: "POST",
      body: JSON.stringify({ name, domain }),
    }),
  /** Supprime une entreprise (cascade domaines/membres/mémoire/corpus côté back). */
  deleteCompany: (id: string) =>
    request<{ deleted: boolean }>(`/knowledge-graph/companies/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  /** Édite la fiche entreprise (nom/site/secteur/localisation/statut) (#2). */
  updateCompany: (
    id: string,
    patch: {
      name?: string;
      websiteUrl?: string | null;
      sector?: string | null;
      location?: string | null;
      relationStatus?: string | null;
    },
  ) =>
    request<{ updated: boolean }>(`/knowledge-graph/companies/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  /** Ajoute une note de mémoire sur une entreprise (#5). */
  addCompanyMemory: (companyId: string, content: string, kind = "note") =>
    request<{ memoryId: string }>(
      `/knowledge-graph/companies/${encodeURIComponent(companyId)}/memory`,
      { method: "POST", body: JSON.stringify({ content, kind }) },
    ),
  /** Rattache une personne à une entreprise (kg_membership). */
  linkCompany: (personId: string, companyId: string, role?: string) =>
    request<{ created: boolean }>("/knowledge-graph/memberships", {
      method: "POST",
      body: JSON.stringify({ personId, companyId, role }),
    }),
  /** Détache une personne d'une entreprise (supprime le lien). */
  unlinkCompany: (personId: string, companyId: string) =>
    request<{ removed: boolean }>("/knowledge-graph/memberships/unlink", {
      method: "POST",
      body: JSON.stringify({ personId, companyId }),
    }),
  /**
   * Associe une entreprise « par URL » (façon Attio) : déduit le domaine, scrape
   * le logo, et (si personId) rattache le contact. Renvoie l'entreprise résolue.
   */
  createCompanyFromUrl: (url: string, personId?: string) =>
    request<{
      companyId: string;
      domain: string;
      name: string;
      website: string;
      hasLogo: boolean;
    }>("/knowledge-graph/companies/from-url", {
      method: "POST",
      body: JSON.stringify({ url, personId }),
    }),
  /** URL du logo d'une entreprise (404 neutre si aucun logo en cache). */
  companyLogoUrl: (companyId: string) =>
    `${API_BASE}/api/knowledge-graph/companies/${encodeURIComponent(companyId)}/logo`,
  // --- CRM nav (#48/#49) : dossiers consolidés marque/contact (lecture seule) -
  /**
   * Dossier consolidé d'une marque en UN appel (#49) : domaines + équipe +
   * mémoire + opt-in IA (remplace colleagues + companyMemory).
   */
  companyDossier: (companyId: string) =>
    request<KgCompanyDossier>(
      `/knowledge-graph/companies/${encodeURIComponent(companyId)}/dossier`,
    ),
  /**
   * Dossier consolidé d'un contact en UN appel (#49) : coordonnées + identités +
   * entreprises + collègues + mémoire (remplace personContext).
   */
  personDossier: (personId: string) =>
    request<KgPersonDossier>(`/knowledge-graph/persons/${encodeURIComponent(personId)}/dossier`),
};

export interface KgCompany {
  id: string;
  name: string;
  source: string;
  websiteUrl?: string | null;
  sector?: string | null;
  location?: string | null;
  relationStatus?: string | null;
  updatedAt: string;
}
export interface KgColleague {
  personId: string;
  displayName: string;
  role: string;
  source: string;
}
export interface KgMemoryNote {
  id: string;
  kind: string;
  content: string;
  source?: string;
}
/** Un extrait de corpus pertinent (présent si l'appel passe un `q`). */
export interface KgCorpusHit {
  content: string;
  score: number;
}
/** Dossier consolidé d'un contact (GET /persons/:id/dossier ; sans q : pas d'appel API). */
export interface KgPersonDossier {
  personId: string;
  displayName: string;
  primaryEmail: string | null;
  primaryPhone: string | null;
  identities: Array<{ network: string; handle?: string; displayName?: string }>;
  companies: Array<{ companyId: string; name: string; role?: string; optedIn?: boolean }>;
  colleagues: Array<{ personId: string; displayName: string; role?: string }>;
  memory: Array<{ kind: string; content: string }>;
  relevant: KgCorpusHit[];
  optedIn: boolean;
}
/** Dossier consolidé d'une marque (GET /companies/:id/dossier). */
export interface KgCompanyDossier {
  companyId: string;
  name: string;
  source: string;
  websiteUrl: string | null;
  sector: string | null;
  location: string | null;
  relationStatus: string | null;
  optedIn: boolean;
  domains: string[];
  team: KgColleague[];
  memory: KgMemoryNote[];
  relevant: KgCorpusHit[];
}

export const telegram = {
  setup: (apiId: string, apiHash: string, phone: string) =>
    request<{ state: TelegramState }>("/messaging/telegram/setup", {
      method: "POST",
      body: JSON.stringify({ apiId, apiHash, phone }),
    }),
  code: (code: string) =>
    request<{ state: TelegramState }>("/messaging/telegram/code", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  password: (password: string) =>
    request<{ state: TelegramState }>("/messaging/telegram/password", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  status: () => request<{ state: TelegramState }>("/messaging/telegram/status"),
};
